/**
 * External store for Default receipt uiLocale auto-sync status (G3).
 * Hook writes; Account settings (ReceiptLocalesBlock) reads + Retry.
 */

export type ReceiptLocaleSyncStatus = 'idle' | 'syncing' | 'error';

type Listener = () => void;

let status: ReceiptLocaleSyncStatus = 'idle';
let lastError: unknown = null;
const listeners = new Set<Listener>();

/** Registered by useSyncReceiptLocaleOnLanguageChange while mounted. */
let retryHandler: (() => void) | null = null;

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeReceiptLocaleSyncStatus(listener: Listener): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

export function getReceiptLocaleSyncStatusSnapshot(): ReceiptLocaleSyncStatus {
  return status;
}

export function getLastReceiptLocaleSyncError(): unknown {
  return lastError;
}

export function setReceiptLocaleSyncStatus(
  next: ReceiptLocaleSyncStatus,
  error: unknown = null,
): void {
  const nextError = next === 'error' ? error : null;
  if (status === next && lastError === nextError) {
    return;
  }
  status = next;
  lastError = nextError;
  emit();
}

/**
 * Hook registers a live-uiLocale re-enqueue callback.
 * Pass null on unmount.
 */
export function registerReceiptLocaleSyncRetry(handler: (() => void) | null): void {
  retryHandler = handler;
}

/** Clear error and re-enqueue live UI locale sync (via registered hook handler). */
export function requestReceiptLocaleSyncRetry(): void {
  lastError = null;
  if (status !== 'idle') {
    status = 'idle';
    emit();
  }
  retryHandler?.();
}

/** Test / HMR helper. */
export function resetReceiptLocaleSyncStatusStore(): void {
  status = 'idle';
  lastError = null;
  retryHandler = null;
  emit();
}
